import React, { FunctionComponent, useEffect, useState } from "react"

const BAR_HEIGHT = 160

const GRADIENT = "linear-gradient(to bottom, #2CAD9B, #151D1C)"
const TRACK_COLOR = "rgba(40, 202, 180, 0.1)"
const MAX_INDICATOR_COLOR = "rgba(40, 202, 180, 0.08)"
const FILL_SHADOW =
    "0 4px 12px 4px rgba(39, 195, 173, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.1)" /* white-200 */

const prefersReducedMotion = () =>
    // the typeof guard is an SSR guard: `window?.` would throw ReferenceError on the server
    // oxlint-disable-next-line typescript/prefer-optional-chain
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches

const LegendPill: FunctionComponent<{ average?: boolean }> = ({ average = false }) => (
    <div
        className="w-2 h-5 rounded"
        style={{ background: average ? GRADIENT : MAX_INDICATOR_COLOR }}
    />
)

interface ThermometerBarProps {
    label: string
    unit: string
    average: number
    max: number
    animated?: boolean
    animationDuration?: number
}

export const ThermometerBar: FunctionComponent<ThermometerBarProps> = ({
    label,
    unit,
    average,
    max,
    animated = true,
    animationDuration = 600,
}) => {
    const averageRatio = max > 0 ? Math.min(average / max, 1) : 0
    const averageHeightPx = Math.round(averageRatio * BAR_HEIGHT)

    const shouldAnimate = animated && !prefersReducedMotion()

    const [risen, setRisen] = useState(false)

    useEffect(() => {
        if (!shouldAnimate) return
        const t = setTimeout(() => setRisen(true), 10)
        return () => clearTimeout(t)
    }, [shouldAnimate])

    return (
        <div className="flex flex-col items-center gap-2">
            <small className="text-gray-300">
                {max} {unit}
            </small>

            <div
                className="w-9 rounded-full flex items-end"
                style={{ height: BAR_HEIGHT, background: TRACK_COLOR }}
            >
                <div
                    className="w-full origin-bottom"
                    style={{
                        height: averageHeightPx,
                        borderRadius: `${Math.min(18, averageHeightPx / 2)}px ${Math.min(18, averageHeightPx / 2)}px 18px 18px`,
                        background: GRADIENT,
                        boxShadow: FILL_SHADOW,
                        transform: shouldAnimate ? (risen ? "scaleY(1)" : "scaleY(0)") : undefined,
                        transition: shouldAnimate
                            ? `transform ${animationDuration}ms cubic-bezier(0.16, 1, 0.3, 1)`
                            : undefined,
                    }}
                />
            </div>

            <small className="text-gray-300 text-center">{label}</small>

            <div className="flex items-center gap-1.5">
                <LegendPill average />
                <small className="text-gray-300">
                    {average} {unit}
                </small>
            </div>
        </div>
    )
}

export const EmptyThermometerBar: FunctionComponent<Pick<ThermometerBarProps, "label">> = ({
    label,
}) => {
    return (
        <div className="flex flex-col items-center gap-2">
            <small className="text-gray-300">--</small>
            <div
                className="w-9 rounded-full flex items-end"
                style={{ height: BAR_HEIGHT, background: TRACK_COLOR }}
            />
            <small className="text-gray-300 text-center">{label}</small>
            <small className="text-gray-300">--</small>
        </div>
    )
}

export const ThermometerChartLegend: FunctionComponent = () => (
    <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
            <LegendPill average />
            <small className="text-gray-300">Average</small>
        </div>
        <div className="flex items-center gap-1.5">
            <LegendPill />
            <small className="text-gray-300">Max</small>
        </div>
    </div>
)
